import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PurchaseOrder } from '../types';
import { purchaseOrderAPI } from '../services/api';

const PurchaseOrderList: React.FC = () => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await purchaseOrderAPI.getAll();
      setPurchaseOrders(response.data.results);
      setError(null);
    } catch (err) {
      setError('Failed to fetch purchase orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this purchase order?')) {
      try {
        await purchaseOrderAPI.delete(id);
        fetchPurchaseOrders();
      } catch (err) {
        setError('Failed to delete purchase order');
        console.error(err);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (value: string | undefined) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(value));
  };

  const getFundsColor = (po: PurchaseOrder) => {
    if (!po.total_value || !po.remaining_funds) return '#95a5a6';
    const utilization = parseFloat(po.funds_utilization_percentage || '0');
    if (utilization >= 90) return '#e74c3c'; // Red
    if (utilization >= 75) return '#f39c12'; // Orange
    return '#27ae60'; // Green
  };

  const getPopColor = (po: PurchaseOrder) => {
    if (po.is_pop_expired) return '#e74c3c'; // Red
    if (po.is_pop_expiring_soon) return '#f39c12'; // Orange
    return '#2c3e50'; // Default dark
  };

  if (loading) return <div className="loading">Loading purchase orders...</div>;

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Purchase Orders</h2>
          <Link to="/purchase-orders/new" className="btn btn-primary">
            Create New PO
          </Link>
        </div>
        
        {error && <div className="error">{error}</div>}
        
        {purchaseOrders.length === 0 ? (
          <p>No purchase orders found. Create your first PO to get started.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Company</th>
                  <th>Projects</th>
                  <th>Total Value</th>
                  <th>Remaining</th>
                  <th>Period of Performance</th>
                  <th>Status</th>
                  <th>Alerts</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrders.map((po) => (
                  <tr key={po.id}>
                    <td><strong>{po.po_number}</strong></td>
                    <td>{po.customer_details?.name || `Customer #${po.customer}`}</td>
                    <td>
                      {po.projects_details && po.projects_details.length > 0
                        ? po.projects_details.map(p => p.code).join(', ')
                        : '-'}
                    </td>
                    <td>{formatCurrency(po.total_value)}</td>
                    <td>
                      <span style={{ color: getFundsColor(po), fontWeight: 'bold' }}>
                        {formatCurrency(po.remaining_funds)}
                      </span>
                    </td>
                    <td>
                      {po.start_date && po.end_date ? (
                        <div style={{
                          fontSize: '0.9rem',
                          color: getPopColor(po),
                          fontWeight: (po.is_pop_expired || po.is_pop_expiring_soon) ? 'bold' : 'normal'
                        }}>
                          {formatDate(po.start_date)} - {formatDate(po.end_date)}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`status-badge status-${po.status}`}>
                        {po.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {po.is_funds_low && (
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#fee',
                            color: '#c0392b',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap'
                          }}>
                            💰 Low Funds
                          </span>
                        )}
                        {po.is_pop_expired ? (
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#fee',
                            color: '#c0392b',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap'
                          }}>
                            🚫 PoP Expired
                          </span>
                        ) : po.is_pop_expiring_soon && (
                          <span style={{
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#fef5e7',
                            color: '#d68910',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap'
                          }}>
                            ⚠️ PoP {po.days_until_pop_expires}d
                          </span>
                        )}
                        {!po.is_funds_low && !po.is_pop_expiring_soon && !po.is_pop_expired && '-'}
                      </div>
                    </td>
                    <td className="actions">
                      <Link
                        to={`/purchase-orders/${po.id}`}
                        className="btn btn-primary btn-small"
                      >
                        View
                      </Link>
                      <Link
                        to={`/purchase-orders/${po.id}/edit`}
                        className="btn btn-secondary btn-small"
                      >
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(po.id!)}
                        className="btn btn-danger btn-small"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderList;
